import { getFlightItems } from 'checkout/selectors/view/flights'
import { getBedbankItems, getLeHotelItems } from 'checkout/selectors/view/hotels'
import { excludeNullOrUndefined } from 'checkout/utils'
import { BnblTotalPakcagesPrices, DEFAULT_OCCUPANCY_FOR_BNBL } from 'components/OfferPage/BnblModal/utils'
import { OfferDurationOption } from 'components/OfferPage/Filters/DurationTabs/OfferDurationTabs'
import config from 'constants/config'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { maxFlightPassengerCount } from 'constants/flight'
import { RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import { BundledOfferState, coverByOffersStates, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { arrayToObject, countWhere, fillArray, groupBy, min, sortBy, sum, trimArray, unique } from 'lib/array/arrayUtils'
import { buildParamsKey } from 'lib/offer/availabilityUtils'
import { getBundlePackagesWithPrice } from 'lib/bundleAndSave/getBundlePackagesWithPrice'
import { getGroupedBundledPackages } from 'lib/bundleAndSave/getGroupedBundledPackages'
import { getSumExtraGuestsSurcharge, isReservationAvailableForRoom } from 'lib/checkout/cartReservationUtils'
import createSelector from 'lib/web/createSelector'
import { getLowestPricePackage } from 'lib/offer/getLowestPricePackage'
import roomRateTacticalScheduleMemoiser from 'lib/hotels/roomRateTacticalScheduleMemoiser'
import { generateRoomOccupants } from 'lib/offer/occupancyUtils'
import { isBundleOffer, isLEHotel, isVillaOffer } from 'lib/offer/offerTypes'
import { getBedbankAndFlightsChildMaxAge, getDurationLabel, getOfferAndFlightsMaxChildAge } from 'lib/offer/offerUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { hasSellableSchedule, hasTravellableSchedules, scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import { decodeSearchParams, isRoomsInvalid } from 'lib/search/searchUtils'
import moment from 'moment'
import { getPromotionMinLos } from 'selectors/offerPage/bedbankOfferPageSelectors'
import { getCalenderDaysByCheckIn } from '../cart/cartCalendarSelectors'
import { multiRoomEnabled, shouldEnableCalendarRecommendations } from '../featuresSelectors'
import { getDefaultAirport } from '../flightsSelectors'
import { inclusionTravelPeriodFilter } from 'lib/offer/inclusionsTravelPeriodFilter'
import { convertMarkdownInclusionsToArray } from 'lib/string/markdownUtils'
import { getVillaItems } from 'checkout/selectors/view/villa'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'

export interface BaseOfferPackageOptionView {
  sortOrder: number;
  basePrice: number;
  baseValue: number;
  roomPrices: number[];
  surcharge: number;
  extraGuestsSurcharge: number;
  taxesAndFees: number;
  propertyFees?: number;
  hotelPrice: number;
  flightPrice: number;
  capacityValid: boolean;
  hotelAvailable: boolean;
  flightsAvailable: boolean;
  hotelValue: number;
  hotelDiscountPercent: number;
  bundledDiscountPercent: number;
  availableRooms?: number;
  fromCalendarInfo: boolean;
  hasAvailableRates?: boolean;
  hasTactical?: boolean;
  schedules?: App.ReservationSchedule[];
}

export interface OfferPackageOptionView extends BaseOfferPackageOptionView {
  package: App.Package;
}

export interface HotelOfferPackageOptionView extends OfferPackageOptionView {
  package: App.HotelPackage;
}

export interface SortOrderDefinedOfferPackageOptionView extends OfferPackageOptionView {
  package: Omit<App.Package, 'sortOrder'> & { sortOrder: number };
}

export interface BundleOfferPackageOptionView extends BaseOfferPackageOptionView {
  bundlePackageId: string
  bundlePackageName: string
  options: OfferPackageOptionView[];
}

export const getBundledPackages = createSelector(
  (state: OfferPageState) => state.bundleOffer,
  (state: OfferPageState) => state.bundledOffers,
  (bundleOffer, bundledOffers) => {
    if (bundleOffer && bundledOffers) {
      return getGroupedBundledPackages(bundleOffer, Object.values(bundledOffers).map((bOffer) => ({
        offerId: bOffer.offer.id,
        duration: bOffer.duration,
      })))
    }

    return null
  },
)

export const getComplementaryBundleState = createSelector(
  (state: OfferPageState) => state.paginator,
  (state: OfferPageState) => state.bundledOffers,
  (paginator, bundledOffers) => {
    return bundledOffers && paginator ? Object.values(bundledOffers).find(o => o.offer.id !== paginator.currentOfferId) : {}
  },
)

export const getRelevantPackages = createSelector(
  (state: OfferPageState, appState: App.State) => getBasePackageViews(state, appState),
  (state: OfferPageState) => state.duration,
  getBundledPackages,
  (packageViews, duration, bundlePackages) => {
    if (bundlePackages) {
      const packagesData = Object.values(bundlePackages).flat()
      const allAllowPackages = packagesData.map(pkg => pkg.uniqueKey)

      return packageViews.filter(view => allAllowPackages.includes(view.package.uniqueKey) && view.package.duration === duration && view.capacityValid)
    }

    return packageViews.filter(view => view.package.duration === duration && view.capacityValid)
  },
)

export const getRelevantRoomRateIds = createSelector(
  (_state: App.State, offerState: OfferPageState) => offerState.offer,
  (state: App.State, offerState: OfferPageState) => getRelevantPackages(offerState, state),
  (offer, packageViews) => {
    const packagedViews = packageViews.filter(view => view.package.roomRate.isPackaged)
    const packageViewsByDuration = packageViews.filter(view => !view.package.roomRate.isPackaged)

    if (!offer.isDomestic) {
      for (const view of packagedViews) {
        if (view.hotelPrice && view.hotelAvailable) {
          const indexPackageViewByDuration = packageViewsByDuration.findIndex(x => x.package.roomType?.id === view.package.roomType?.id && x.package.roomRate?.packagedRatePlanId === view.package.roomRate?.ratePlanId)
          if (indexPackageViewByDuration >= 0) {
            packageViewsByDuration[indexPackageViewByDuration] = view
          } else {
            packageViewsByDuration.push(view)
          }
        }
      }
    }

    return Array.from(new Set(packageViewsByDuration.map(x => x.package.roomRate?.id)).values())
  },
)

export const getRelevantPackageCalendars = createSelector(
  (state: OfferPageState, appState: App.State) => appState.calendar.calendarsByPackageKey,
  (state: OfferPageState, appState: App.State) => getRelevantPackages(state, appState),
  (state: OfferPageState, appState: App.State) => getTacticalScheduleMap(state, appState),
  (calendars, packageViews, isTacticalRate) => {
    return packageViews.map(p => {
      const calendarData = calendars[p.package.uniqueKey]

      if (calendarData) {
        calendarData.months.forEach((m) => {
          m.days.forEach((d) => {
            d.hasTactical = isTacticalRate(d.roomRateId, d.checkIn)
          })
        })
      }

      return calendarData
    }).filter(Boolean)
  },
)

export const getRelevantMonths = createSelector(
  (state: App.State, offerState: OfferPageState) => getRelevantPackageCalendars(offerState, state),
  (calendars) => {
    return calendars.flatMap(c => c.months)
  },
)

export const getRelevantDays = createSelector(
  (state: App.State, offerState: OfferPageState) =>
    getRelevantMonths(state, offerState),
  (relevantMonths) => {
    return relevantMonths.flatMap(c => c.days)
  },
)

/**
 * This selector returns all the package views without any consideration for the currently
 * selected check in date.
 */
export const getBasePackageViews = createSelector(
  (state: OfferPageState) => state.offer!,
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.airport,
  (_state: OfferPageState, appState: App.State) => appState.calendar.calendarsByPackageKey,
  (_state: OfferPageState, appState: App.State) => appState.geo.currentRegionCode,
  (offer, rooms, originAirport, calendars, regionCode): Array<OfferPackageOptionView> => {
    let packages = offer.packages
    if (offer.packages.length === 0 && offer.lowestPricePackage) {
      packages = [offer.lowestPricePackage]
    }

    const offerAvailable = scheduleIsCurrent(offer.onlinePurchaseSchedule)
    const isPriceTaxExclusive = isEnabledForFeature(config.TAX_EXCLUSIVE_REGIONS, regionCode)

    const numberOfRooms = rooms?.length ?? 1
    return packages.map((pkg, index) => {
      const calendar = calendars[pkg.uniqueKey]
      const cheapestDay = calendar?.cheapestDay
      const cheapestDayWithFlights = calendar?.cheapestDayWithFlights
      // if we have calendar info but no cheapest day, means nothing was available at all
      const baseIsAvailable = offerAvailable && !!pkg.price && !(calendar && !cheapestDay)

      const roomPrices = cheapestDay?.roomPrices
      const basePrice = cheapestDay?.hotelPrice ?? (pkg.price * numberOfRooms)
      const baseValue = cheapestDay?.hotelValue ?? (pkg.value * numberOfRooms)
      const surcharge = cheapestDay?.surcharge ?? pkg.surcharge ?? 0
      const taxesAndFees = cheapestDay?.taxesAndFees ?? (pkg.taxesAndFees * numberOfRooms)
      const propertyFees = cheapestDay?.propertyFees ?? (pkg.propertyFees * numberOfRooms)
      const flightCost = !originAirport ? 0 : (calendar?.cheapestDayWithFlights?.flightPrice) ?? offer.flightPrices[originAirport.code] ?? 0
      const hotelAvailable = baseIsAvailable && (cheapestDay?.hotelAvailable ?? true) && hasSellableSchedule(pkg.roomRate?.schedules ?? [])
      const flightsAvailable = baseIsAvailable && (cheapestDayWithFlights?.flightsAvailable ?? true)
      const extraGuestsSurcharge = getSumExtraGuestsSurcharge(pkg, rooms, offer)
      const hotelPrice = basePrice + surcharge + extraGuestsSurcharge
      const hotelValue = baseValue + surcharge + extraGuestsSurcharge

      // Discount
      const includeTaxPrice = (isPriceTaxExclusive ? basePrice + taxesAndFees : basePrice) + surcharge + extraGuestsSurcharge
      const includeTaxValue = baseValue + surcharge + extraGuestsSurcharge

      const baseBundledValue = includeTaxValue + flightCost
      const baseBundledPrice = includeTaxPrice + flightCost

      const hotelDiscountPercent = Math.round((includeTaxValue - includeTaxPrice) * 100 / includeTaxValue)
      const bundledDiscountPercent = Math.round((baseBundledValue - baseBundledPrice) * 100 / baseBundledValue)

      let totalPassengers = 0

      if (rooms) {
        totalPassengers = sum(rooms, room => room.adults + room.children + room.infants)
      }
      return {
        sortOrder: index + 1,
        package: pkg,
        hotelPrice,
        hotelValue,
        roomPrices,
        taxesAndFees,
        propertyFees,
        basePrice,
        baseValue,
        surcharge,
        extraGuestsSurcharge,
        flightPrice: flightCost,
        capacityValid: !rooms || rooms.every(room => isReservationAvailableForRoom(room, pkg.roomRate.capacities, offer)),
        hotelAvailable,
        flightsAvailable: flightsAvailable && totalPassengers <= maxFlightPassengerCount,
        hotelDiscountPercent,
        bundledDiscountPercent,
        fromCalendarInfo: !!calendar,
        hasTactical: pkg.hasTactical ?? false,
        schedules: pkg.roomRate?.schedules ?? [],
      }
    })
  })

/**
 * This selector returns the package views while taking into consideration
 * the current check in date.
 */
export const mapPackageViewCheckIn = (view, calendars, checkIn, isPriceTaxExclusive) => {
  const calendar = calendars[view?.package.uniqueKey]
  if (calendar) {
    const isoCheckin = checkIn.format(ISO_DATE_FORMAT)
    const day = getCalenderDaysByCheckIn(calendar)[isoCheckin]
    if (day) {
      // Discount
      const includeTaxPrice = (isPriceTaxExclusive ? day.hotelPrice + day.taxesAndFees ?? 0 : day.hotelPrice) + day.surcharge + view.extraGuestsSurcharge
      const includeTaxValue = day.hotelValue + day.surcharge + view.extraGuestsSurcharge

      const bundledValue = includeTaxValue + day.flightPrice
      const bundledPrice = includeTaxPrice + day.flightPrice

      const hotelValue = day.hotelValue + day.surcharge + view.extraGuestsSurcharge
      const hotelPrice = day.hotelPrice + day.surcharge + view.extraGuestsSurcharge

      return {
        ...view,
        roomPrices: day.roomPrices,
        basePrice: day.hotelPrice,
        flightPrice: day.flightPrice,
        taxesAndFees: day.taxesAndFees,
        propertyFees: day.propertyFees,
        hotelAvailable: view.hotelAvailable && day.hotelAvailable && hasTravellableSchedules(view.schedules, day.checkIn),
        flightsAvailable: view.hotelAvailable && day.hotelAvailable && view.flightsAvailable && day.flightsAvailable,
        surcharge: day.surcharge,
        hotelPrice,
        hotelValue,
        hotelDiscountPercent: Math.round((includeTaxValue - includeTaxPrice) * 100 / includeTaxValue),
        bundledDiscountPercent: Math.round((bundledValue - bundledPrice) * 100 / bundledValue),
        availableRooms: day.availableRooms,
      }
    } else {
      // no day, mustn't be available at all
      return {
        ...view,
        hotelAvailable: false,
        flightsAvailable: false,
      }
    }
  }

  return view
}
const getPackageViews = createSelector(
  (state: OfferPageState, appState: App.State) => getBasePackageViews(state, appState),
  (state: OfferPageState) => state.checkInDate,
  (_state: OfferPageState, appState: App.State) => appState.calendar.calendarsByPackageKey,
  (_state: OfferPageState, appState: App.State) => appState.geo.currentRegionCode,
  (packageViews, checkIn, calendars, regionCode): Array<OfferPackageOptionView> => {
    const isPriceTaxExclusive = isEnabledForFeature(config.TAX_EXCLUSIVE_REGIONS, regionCode)
    if (checkIn) {
      return packageViews.map(view => mapPackageViewCheckIn(view, calendars, checkIn, isPriceTaxExclusive))
    }

    return packageViews
  },
)

/**
 * This selector returns all the package views without any consideration for the currently
 * selected check in date (i.e. the calendar). It also only returns the pricing for 1 room.
 * See function, getBnblPackageViewByNumberOfRooms to get the pricing for multiple rooms.
 */
export const getBnblPackageViews = createSelector(
  (state: OfferPageState) => state.offer,
  (offer): Array<OfferPackageOptionView> => {
    let packages = offer.packages
    if (offer.packages.length === 0 && offer?.lowestPricePackage) {
      packages = [offer.lowestPricePackage]
    }

    const occupancies: App.Occupants[] = [DEFAULT_OCCUPANCY_FOR_BNBL]

    const offerAvailable = scheduleIsCurrent(offer.onlinePurchaseSchedule)
    return packages.map((pkg, index) => {
      const baseIsAvailable = offerAvailable && !!pkg.price
      const basePrice = pkg.price
      const baseValue = pkg.value
      const taxesAndFees = pkg.taxesAndFees
      const propertyFees = pkg.propertyFees
      const hotelAvailable = baseIsAvailable
      const baseBundledValue = baseValue
      const baseBundledPrice = basePrice
      const hotelDiscountPercent = Math.round((baseValue - basePrice) * 100 / baseValue)
      const bundledDiscountPercent = Math.round((baseBundledValue - baseBundledPrice) * 100 / baseBundledValue)
      const hotelPrice = basePrice
      const hotelValue = baseValue

      return {
        sortOrder: index + 1,
        package: pkg,
        hotelPrice,
        hotelValue,
        roomPrices: [],
        taxesAndFees,
        propertyFees,
        basePrice,
        baseValue,
        surcharge: 0,
        extraGuestsSurcharge: 0, // bnbl always assumes cost for just 1 adult, it does not take into consideration extra guests
        flightPrice: 0,
        flightsAvailable: false,
        capacityValid: !occupancies || occupancies.every(room => isReservationAvailableForRoom(room, pkg.roomRate.capacities, offer)),
        hotelAvailable,
        hotelDiscountPercent,
        bundledDiscountPercent,
        fromCalendarInfo: false,
      }
    })
  },
)

/**
 * This selector returns the total prices for all the package views inside bnblModal
 * that will be displayed on BNBL modals footer.
 */
export const getBnblPackageViewsTotalPrice = createSelector(
  (state: OfferPageState) => state.offer,
  (state: OfferPageState, appState: App.State) => appState.offer.bnblModal,
  (state: OfferPageState, appState: App.State) => appState.geo.showTaxExclusivePrices,
  (offer, bnblModal, showTaxExclusivePrices): BnblTotalPakcagesPrices => {
    const bnblModalPackages = bnblModal[offer.id]

    const totalRoomsPrice = bnblModalPackages.reduce((acc, modalPackage) => {
      const { hotelPrice, hotelValue, package: pkg, propertyFees } = modalPackage.packageView
      const priceFrom = hotelPrice + propertyFees + (showTaxExclusivePrices ? pkg.taxesAndFees : 0)
      const shouldDisplayValue = !acc.shouldDisplayValue ? acc.shouldDisplayValue : pkg.shouldDisplayValue

      return {
        hotelPrice: acc.hotelPrice + hotelPrice,
        priceFrom: acc.priceFrom + priceFrom,
        hotelValue: acc.hotelValue + hotelValue,
        shouldDisplayValue,
      }
    }, { hotelPrice: 0, priceFrom: 0, hotelValue: 0, shouldDisplayValue: true })

    return totalRoomsPrice
  },
)

/**
 * Retrieves all options for every package (room/rate/group combination)
 */
export const getDurationPackageOptions = createSelector(
  (state: OfferPageState) => state.offer,
  (state: OfferPageState, appState: App.State) => getPackageViews(state, appState),
  (state: OfferPageState) => state.duration,
  (state: OfferPageState, appState: App.State) => appState.offer.offerAvailableRates,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState, appState: App.State) => appState.router.location,
  (state: OfferPageState, appState: App.State) => appState.geo.currentRegionCode,
  (state: OfferPageState, appState: App.State) => getTacticalScheduleMap(state, appState),
  (offer, packageViews, duration, offersAvailableRates, checkIn, rooms, location, regionCode, isTacticalRate): Array<OfferPackageOptionView> => {
    // flag which indicate if this property uses a external providers for checking availability
    const useDynamicCancellationPolicies = offer.property.useDynamicCancellationPolicies ?? false

    const isPriceTaxExclusive = isEnabledForFeature(config.TAX_EXCLUSIVE_REGIONS, regionCode)

    // all available room rates, using only if flag useDynamicCancellationPolicies is true
    const availableRatesKey = checkIn && rooms ? buildParamsKey(checkIn.format(ISO_DATE_FORMAT), checkIn.clone().add(duration, 'days').format(ISO_DATE_FORMAT), rooms) : undefined
    const offerAvailableRates = checkIn && availableRatesKey && offersAvailableRates?.[offer.id]?.[availableRatesKey]

    // all packages filtered by duration
    let packageViewsByDuration = packageViews.filter(view => view.package.duration === duration)

    // all packages filtered by packaged
    const packagedViews = packageViewsByDuration.filter(view => view.package.roomRate.isPackaged)
    packageViewsByDuration = packageViewsByDuration.filter(view => !view.package.roomRate.isPackaged)

    const isAvailableDynamic = (view) => (
      useDynamicCancellationPolicies && offerAvailableRates?.rates?.some(rate => rate.packageUniqueKey === view.package.uniqueKey) ||
      !useDynamicCancellationPolicies)

    const isAvailable = (view) => view.hotelPrice && view.hotelAvailable && (checkIn && isAvailableDynamic(view) || !checkIn)

    const checkInISO = checkIn?.format(ISO_DATE_FORMAT)

    if (!offer.isDomestic) {
      for (const view of packagedViews) {
        if (isAvailable(view)) {
          const indexPackageViewByDuration = packageViewsByDuration.findIndex(x => x.package.roomType?.id === view.package.roomType?.id && x.package.roomRate?.packagedRatePlanId === view.package.roomRate?.ratePlanId)
          if (indexPackageViewByDuration >= 0) {
            packageViewsByDuration[indexPackageViewByDuration] = view
          } else {
            packageViewsByDuration.push(view)
          }
        }
      }
    }

    // grouped packages by roomTypeId and groupId
    const groupedPackageViews = groupBy(packageViewsByDuration.filter(view => view.package.roomType?.id && view.package.roomRate?.group?.id), view => `${view.package.roomType.id}-${view.package.roomRate.group.id}`)

    let newPackageViews: any[]

    if (location?.query?.utm_campaign === 'google-hotel-ads') {
      newPackageViews = packageViewsByDuration
    } else {
      // all rest packages which have not groups
      newPackageViews = packageViewsByDuration.filter(view => !view.package.roomRate?.group?.id)
      // finding an available lowest price into each group
      for (const views of groupedPackageViews.values()) {
        // find the lowest price package in current group
        const lowestPricePackage = min(views.filter(view => view.hotelPrice), view => view.hotelPrice) ?? views[0]

        // find all available packages in current group
        const hotelAvailablePackages = views.filter(isAvailable)

        // find the lowest price package in hotelAvailablePackages
        const hotelAvailablePackage = min(hotelAvailablePackages, view => view.hotelPrice)

        // add the lowest price package to other packages without group
        newPackageViews.push(hotelAvailablePackage ?? lowestPricePackage)
      }
    }

    newPackageViews = newPackageViews.map(p => ({
      ...p,
      hasTactical: checkIn ? isTacticalRate(p.package.roomRate?.id, checkIn.format(ISO_DATE_FORMAT)) : p.hasTactical,
    }))

    // substitute data of view from availability endpoint
    if (useDynamicCancellationPolicies) {
      return newPackageViews.map((view) => {
        const availableData = offerAvailableRates?.rates?.find(rate => rate.packageUniqueKey === view.package.uniqueKey)
        if (availableData) {
          const newView = {
            ...view,
            hotelAvailable: view.hotelAvailable && ((checkInISO && hasTravellableSchedules(view.package.schedules, checkInISO)) || !checkIn),
            basePrice: availableData.hotelPrice,
            baseValue: availableData.hotelValue,
            roomPrices: availableData.roomPrices,
            surcharge: availableData.surcharge ?? 0,
            extraGuestsSurcharge: availableData.extraGuestSurcharge ?? 0,
            hotelPrice: availableData.hotelPrice + view.surcharge + view.extraGuestsSurcharge,
            hotelValue: availableData.hotelValue + view.surcharge + view.extraGuestsSurcharge,
            flightPrice: availableData.flightPrice ?? 0,
            taxesAndFees: availableData.taxesAndFees ?? 0,
            propertyFees: availableData.propertyFees ?? 0,
            availableRooms: availableData.availableRooms,
            hasAvailableRates: true,
          }

          // Discount
          const includeTaxPrice = (isPriceTaxExclusive ? newView.hotelPrice + newView.taxesAndFees ?? 0 : newView.hotelPrice) + newView.surcharge + newView.extraGuestsSurcharge
          const includeTaxValue = newView.hotelValue + newView.surcharge + newView.extraGuestsSurcharge

          const baseBundledValue = includeTaxValue + newView.flightPrice
          const baseBundledPrice = includeTaxPrice + newView.flightPrice

          const hotelDiscountPercent = Math.round((includeTaxValue - includeTaxPrice) * 100 / includeTaxValue)
          const bundledDiscountPercent = Math.round((baseBundledValue - baseBundledPrice) * 100 / baseBundledValue)

          newView.hotelDiscountPercent = hotelDiscountPercent
          newView.bundledDiscountPercent = bundledDiscountPercent

          return newView
        }

        return view
      })
    }

    return newPackageViews
  },
)

export const getCheapestRoomOptions = createSelector(
  getDurationPackageOptions,
  (packageOptions) => {
    const packagesByRoom = groupBy(packageOptions, p => p.package.roomType.id)

    return [...packagesByRoom.values()].map(options => min(options, option => option.hotelPrice ? option.hotelPrice : Number.MAX_SAFE_INTEGER))
  },
)

export const getBundleDurationPackageOptions = createSelector(
  (state: OfferPageState, appState: App.State) => coverByOffersStates<OfferPackageOptionView>(getDurationPackageOptions, state, appState).flat(),
  getBundledPackages,
  (packageOptions, bundlePackages): { [bundlePackageId: string]: OfferPackageOptionView[] } => {
    if (bundlePackages) {
      return Object.keys(bundlePackages).reduce((acc, bundlePackageId) => {
        const packagesData = bundlePackages[bundlePackageId]
        const allAllowPackages = packagesData.map(pkg => pkg.uniqueKey)

        acc[bundlePackageId] = packageOptions.filter(view => allAllowPackages.includes(view.package.uniqueKey))
        return acc
      }, {})
    }

    return null
  },
)

export const getAvailableBundlePackages = createSelector(
  (state: OfferPageState, appState: App.State) => getBundleDurationPackageOptions(state, appState),
  getBundledPackages,
  (state: OfferPageState) => state.offer,
  (state: OfferPageState) => state.bundledOffers,
  (packageOptions, bundlePackages, offer, stateBundledOffers = {}): { [bundlePackageId: string]: BundleOfferPackageOptionView } => {
    if (!bundlePackages) {
      return null
    }

    return Object.keys(bundlePackages).reduce<Record<string, BundleOfferPackageOptionView>>((acc, bundlePackageId, index) => {
      const packagesData = bundlePackages[bundlePackageId]
      const allAllowPackages = packagesData.map(pkg => pkg.uniqueKey)
      const options = packageOptions[bundlePackageId].filter((o) => allAllowPackages.includes(o.package.uniqueKey))
      const bundledOffersIds = Object.keys(offer.bundledOffers)

      const hotelAvailable = options.length === bundledOffersIds.length ? options.every((o) => {
        const bundledOffer = offer.bundledOffers[o.package.offerId]
        const useDynamicCancellationPolicies = bundledOffer.property.useDynamicCancellationPolicies ?? false

        const isAvailable = o.hotelPrice && o.hotelAvailable

        if (useDynamicCancellationPolicies && stateBundledOffers[o.package.offerId]?.checkInDate) {
          return isAvailable && o.hasAvailableRates
        }

        return isAvailable
      }) : false

      acc[bundlePackageId] = {
        bundlePackageId,
        sortOrder: offer.bundlePackages[bundlePackageId].sortOrder || index,
        options,
        bundlePackageName: offer.bundlePackages[bundlePackageId].name,
        basePrice: 0,
        baseValue: 0,
        roomPrices: [],
        surcharge: 0,
        extraGuestsSurcharge: 0,
        hotelPrice: 0,
        hotelValue: 0,
        flightPrice: 0,
        taxesAndFees: 0,
        propertyFees: 0,
        hotelDiscountPercent: 0,
        bundledDiscountPercent: 0,
        availableRooms: min(options, option => option.availableRooms)?.availableRooms ?? 0,
        hotelAvailable,
        capacityValid: options.length === bundledOffersIds.length ? options.every((o) => o.capacityValid) : false,
        flightsAvailable: false,
        fromCalendarInfo: false,
        hasAvailableRates: undefined,
      }

      for (const option of options) {
        const hotelPrice = acc[bundlePackageId].hotelPrice + option.hotelPrice
        const hotelValue = acc[bundlePackageId].hotelValue + option.hotelValue
        const hotelDiscountPercent = Math.round((hotelValue - hotelPrice) * 100 / hotelValue)
        acc[bundlePackageId] = {
          ...acc[bundlePackageId],
          basePrice: acc[bundlePackageId].basePrice + option.basePrice,
          baseValue: acc[bundlePackageId].baseValue + option.baseValue,
          roomPrices: [...acc[bundlePackageId].roomPrices, ...(option.roomPrices ?? [])],
          surcharge: acc[bundlePackageId].surcharge + (option.surcharge ?? 0),
          extraGuestsSurcharge: acc[bundlePackageId].extraGuestsSurcharge + (option.extraGuestsSurcharge ?? 0),
          hotelPrice,
          hotelValue,
          taxesAndFees: acc[bundlePackageId].taxesAndFees + (option.taxesAndFees ?? 0),
          propertyFees: acc[bundlePackageId].propertyFees + (option.propertyFees ?? 0),
          hotelDiscountPercent,
          bundledDiscountPercent: hotelDiscountPercent,
        }
      }
      return acc
    }, {})
  },
)

export const getFilteredBundlePackage = createSelector(
  (pageState: OfferPageState, appState: App.State) => getAvailableBundlePackages(pageState, appState),
  (pageState: OfferPageState) => pageState.filteredBundlePackageId,
  (bundlePackages, filteredPackageId) => Object.values(bundlePackages).find(p => p.bundlePackageId === filteredPackageId),
)

/**
 * Retrieves cheapest option for each unique room
 * Similar to get package options above but at a room level
 */
export const getPropertyRoomOptions = createSelector(
  (state: OfferPageState, appState: App.State) => getPackageViews(state, appState),
  (state: OfferPageState) => state.duration,
  (packageViews, duration): Array<OfferPackageOptionView> => {
    const applicableViews = packageViews.filter(view => view.package.duration === duration)
    const viewsByRoomId = groupBy(applicableViews, view => view.package.roomType.id)

    return [...viewsByRoomId.values()].map(views => {
      const availableViews = views.filter(view => view.capacityValid && view.hotelAvailable)
      return min(availableViews, views => views.hotelPrice) ?? views[0]
    })
  },
)

export const getAvailablePackageOptions = createSelector(
  (state: OfferPageState, appState: App.State) => getPackageViews(state, appState),
  (packageOptions): Array<OfferPackageOptionView> => packageOptions.filter(option => option.hotelAvailable && option.capacityValid),
)

export const getCheapestAvailablePackageOption = createSelector(
  getDurationPackageOptions,
  getPackageViews,
  (packageOptions, views): OfferPackageOptionView => {
    return min(packageOptions, option => option.hotelPrice ? option.hotelPrice : Number.MAX_SAFE_INTEGER) ?? packageOptions[0] ?? views[0]
  },
)

export const getBaseTacticalDurationPackageOptions = createSelector(
  (state: OfferPageState, appState: App.State) => getBasePackageViews(state, appState),
  (state: OfferPageState) => state.duration,
  (packageOptions, duration): Array<OfferPackageOptionView> => packageOptions.filter(option => (
    (option.hasTactical || (option?.package?.inclusions || []).some(inclusion => inclusion?.hasTactical)) &&
    option.hotelAvailable &&
    option.capacityValid &&
    option.package.duration === duration
  )),
)

export const getTacticalPackageOptions = createSelector(
  (state: OfferPageState, appState: App.State) => getDurationPackageOptions(state, appState),
  (state: OfferPageState, appState: App.State) => getBaseTacticalDurationPackageOptions(state, appState),
  (packageOptions, availablePackageOptions): Array<OfferPackageOptionView> => {
    const availableTacticalOptions = packageOptions.filter(option =>
      (option?.hasTactical || (option?.package?.inclusions || []).some(inclusion => inclusion?.hasTactical)) && option.capacityValid && option.hotelAvailable,
    )

    const options = availableTacticalOptions.length > 0 ? availableTacticalOptions : availablePackageOptions

    const optionsByRatePlanId = groupBy(options, option => {
      const commonInclusions = inclusionTravelPeriodFilter(option.package.inclusions ?? [], undefined, undefined, true)
      const bonusInclusions = inclusionTravelPeriodFilter(option.package.bonusInclusions ?? [], undefined, undefined, true)
      const tacticalInclusions = [...commonInclusions.filter(inclusion => inclusion.hasTactical), ...bonusInclusions.filter(inclusion => inclusion.hasTactical)]

      const allInclusions = [...tacticalInclusions, ...bonusInclusions.filter(inclusion => !inclusion.hasTactical), ...commonInclusions.filter(inclusion => !inclusion.hasTactical)]

      if (allInclusions.length > 0) {
        return allInclusions.map(bonusInclusion => {
          if ((bonusInclusion.descriptionV2List?.length ?? 0) > 0) {
            return bonusInclusion.descriptionV2List?.map(n => n.text)
          } else {
            const description = bonusInclusion?.description?.startsWith('-') ?
              bonusInclusion?.description?.slice(1).trim() :
              bonusInclusion.description

            const descriptionLines = description ? convertMarkdownInclusionsToArray(description) : []

            return descriptionLines.map((line) => line)
          }
        }).join('|')
      }

      return option.package.roomRate?.ratePlanId
    })

    return sortBy([...optionsByRatePlanId.values()].map(options => {
      return min(options, option => option.hotelPrice ? option.hotelPrice : Number.MAX_SAFE_INTEGER) ?? options[0]
    }), d => d.hotelPrice, 'asc')
  },
)

export const getMarketedLOS = createSelector(
  getDurationPackageOptions,
  (state: OfferPageState) => state.offer,
  (state: OfferPageState) => state.duration,
  (packageOptions, offer, duration): OfferPackageOptionView => {
    const currentRegionMarketedLos = duration ?? offer.property.currentRegionMarketedLos
    const availableOptions = packageOptions.filter(option => option.package.duration === currentRegionMarketedLos && option.hotelAvailable && option.capacityValid)
    return min(availableOptions, option => option.hotelPrice) ?? packageOptions[0]
  })

export const getCheapestFilteredPackageOption = createSelector(
  getDurationPackageOptions,
  (state: OfferPageState) => state.duration,
  (state: OfferPageState) => state.flights,
  (packageOptions, duration, flights): OfferPackageOptionView => {
    if (flights) {
      const availableOptions = packageOptions.filter(option => option.package.duration === duration && option.flightsAvailable && option.capacityValid)
      return min(availableOptions, option => option.hotelPrice + option.flightPrice) ?? packageOptions[0]
    }
    const availableOptions = packageOptions.filter(option => option.package.duration === duration && option.hotelAvailable && option.capacityValid)
    return min(availableOptions, option => option.hotelPrice) ?? packageOptions[0]
  },
)

export const getCheapestPropertyRoomOption = createSelector(
  (state: OfferPageState, appState: App.State) => getPropertyRoomOptions(state, appState),
  (state: OfferPageState) => state.flights,
  (packageOptions, flights): OfferPackageOptionView => {
    if (flights) {
      const availableOptions = packageOptions.filter(option => option.flightsAvailable && option.capacityValid)
      return min(availableOptions, option => option.hotelPrice + option.flightPrice) ?? packageOptions[0]
    }
    const availableOptions = packageOptions.filter(option => option.hotelAvailable && option.capacityValid)
    return min(availableOptions, option => option.hotelPrice) ?? packageOptions[0]
  },
)

export const getPackageOptionsByUniqueKey = createSelector(
  (state: OfferPageState, appState: App.State) => getPackageViews(state, appState),
  (views) => arrayToObject(views, p => p.package.uniqueKey),
)

export const getDurationOptions = createSelector(
  (state: OfferPageState, appState: App.State) => getBasePackageViews(state, appState),
  (state: OfferPageState) => state.offer,
  (packageOptions, offer): Array<OfferDurationOption> => {
    const packagesViewsByDuration = [...groupBy(packageOptions, p => p.package.duration).entries()]
    const options = packagesViewsByDuration.map<OfferDurationOption>(([duration, views]) => {
      const applicableViews = views.filter(view => offer.bundledWithFlightsOnly ? view.flightPrice : view.hotelAvailable)
      const cheapestHotel = min(applicableViews, view => view.hotelPrice ? view.hotelPrice : Number.MAX_SAFE_INTEGER)

      const hasBonusInclusionsV1 = views.some(view => view.package.bonusInclusions?.length > 0)
      const hasBonusInclusionsV2 = views.some(view => view.package.inclusionsV2?.some(inclusion => inclusion.style === 'bonus'))

      const hasTactical = views.some(view => view.hasTactical && view.hotelAvailable)

      return {
        duration,
        lowestPrice: cheapestHotel?.hotelPrice,
        discountPercent: cheapestHotel?.hotelDiscountPercent,
        hasBonusInclusions: hasBonusInclusionsV1 || hasBonusInclusionsV2,
        hasTactical,
        soldOut: !cheapestHotel?.hotelPrice,
      }
    })
    return sortBy(options, d => d.duration, 'asc')
  },
)

export const getBundleDurationOptions = createSelector(
  getDurationOptions,
  (state: OfferPageState) => state.offer,
  (state: OfferPageState) => state.bundledOffers,
  (durations, currentPaginatedOffer, bundledOffers): Array<OfferDurationOption> => {
    const sortedBundledOffers = sortBy(Object.values(bundledOffers ?? {}), (p) => {
      return p.checkInDate ? p.checkInDate.toDate().getTime() : 0
    }, 'asc')

    const limits = sortedBundledOffers.map((currentOffer, index) => {
      const nextOffer = sortedBundledOffers[index + 1]
      if (nextOffer?.checkInDate && currentOffer.checkInDate && currentOffer.duration) {
        const currentCheckOut = currentOffer.checkInDate.clone().add(currentOffer.duration, 'days').format(ISO_DATE_FORMAT)
        const maxDuration = moment(nextOffer.checkInDate).diff(currentCheckOut, 'days')

        return {
          offerId: currentOffer.offer?.id,
          duration: currentOffer.duration + maxDuration,
        }
      }

      return {
        offerId: currentOffer.offer?.id,
      }
    }).find((o) => o.offerId === currentPaginatedOffer?.id)

    if (limits?.duration) {
      return durations.filter(x => x.duration <= limits.duration)
    }

    return durations
  },
)

export const getCalendarCheapestDays = createSelector(
  (state: App.State) => state.calendar.calendarsByPackageKey,
  (calendars) => {
    const cheapestDays: { [key: string]: App.CalendarCheapestDay } = {}
    if (calendars) {
      Object.values(calendars).forEach((calendar) => {
        if (calendar.uniqueKey) {
          cheapestDays[calendar.uniqueKey] = {
            hotelPrice: calendar.cheapestDay?.hotelPrice ?? 0,
            hotelTotal: calendar.cheapestDay?.hotelTotal ?? 0,
            hotelValue: calendar.cheapestDay?.hotelValue ?? 0,
            surcharge: calendar.cheapestDay?.surcharge ?? 0,
          }
        }
      })
    }
    return cheapestDays
  },
)

type MonthDayViewMetadata = { year: number, monthIndex: number, tacticalDays: string[] }
export type MonthDayView = [string, Array<App.CalendarDay>, MonthDayViewMetadata]

export const getCalendarDays = createSelector(
  (state: App.State, offerState: OfferPageState) => getRelevantDays(state, offerState),
  (_state: App.State, offerState: OfferPageState) => offerState.package,
  (_state: App.State, offerState: OfferPageState) => offerState.room,
  (state: App.State) => state.calendar.calendarsByPackageKey,
  (state: App.State, offerState: OfferPageState) => shouldEnableCalendarRecommendations(state, offerState.offer),
  (state: App.State, offerState: OfferPageState) => getRelevantRoomRateIds(state, offerState),
  (relevantDays, pkg, room, calendars, enableCalendarRecommendations, relevantRoomRateIds): Array<MonthDayView> => {
    let days: Array<App.CalendarDay> = []
    if (pkg) {
      days = relevantDays.filter(day => day.uniqueKey === pkg.uniqueKey)
    } else if (room) {
      days = relevantDays.filter(day => day.roomId === room.id)
    } else {
      days = relevantDays
    }

    // Filtering days by relevant packages
    if (relevantRoomRateIds.length > 0) {
      days = days.filter(s => relevantRoomRateIds.includes(s.roomRateId))
    }

    // We might still be loading some, make sure we have **all** the calendars needed,
    // otherwise we can't accurately show the lowest price for that day
    const allPackageIds = unique(days.map(d => d.uniqueKey))
    if (allPackageIds.every(id => !!calendars[id])) {
      // now find the cheapest price for each day
      const daysByCheckIn = groupBy(days, day => day.checkIn)
      // It's safe to assert that the result of `min` will be defined, because groupBy will only create groups with at least 1 item
      const values = [...daysByCheckIn.values()].map(dayGroup => min(dayGroup, day => day.hotelAvailable ? day.hotelTotal : Number.MAX_SAFE_INTEGER)!)
      const groupedByTactical = groupBy(days.filter(d => d.hasTactical && d.hotelAvailable), day => day.monthKey)
      const groupedEntries: Array<MonthDayView> = Array.from(groupBy(values, day => `${day.month} ${day.year}`).entries())
        .map(monthGroup => {
          const firstDate = new Date(monthGroup[1][0].checkIn)
          const year = firstDate.getUTCFullYear()
          const monthIndex = firstDate.getUTCMonth()
          const tacticalDays = Array.from(new Set((groupedByTactical.get(`${monthIndex + 1}-${year}`) ?? []).map(x => x.checkIn)).values())
          const metadata = { year, monthIndex, tacticalDays }

          return [monthGroup[0], monthGroup[1], metadata]
        })

      if (enableCalendarRecommendations) {
        // Show unavailable items in this casee
        return groupedEntries
      }
      return trimArray(
        groupedEntries,
        entry => entry[1].every(day => !day.hotelAvailable),
      )
    }

    return []
  },
)

export const getSelectedOfferPageDay = createSelector(
  (state: App.State, offerState: OfferPageState) => getCalendarDays(state, offerState),
  (_state: App.State, offerState: OfferPageState) => offerState.checkInDate,
  (daysByMonth, checkIn) => {
    if (checkIn) {
      const key = checkIn.format('MMMM YYYY')
      const monthDays = daysByMonth.find(entry => entry[0] === key)
      if (monthDays) {
        const checkInDate = checkIn.format(ISO_DATE_FORMAT)
        return monthDays[1].find(day => day.checkIn === checkInDate)
      }
    }
  },
)

export const getCheapestSelectedPackageOption = createSelector(
  (state: App.State, offerState: OfferPageState) => getSelectedOfferPageDay(state, offerState),
  (state: App.State, offerState: OfferPageState) => getDurationPackageOptions(offerState, state),
  (selectedDay, packageViews) => {
    if (selectedDay) {
      return packageViews.find(view => view.package.uniqueKey === selectedDay.uniqueKey)
    }
  },
)

export const getAllPackagesWithIsBaseRate = createSelector(
  (state: App.State, offerState: OfferPageState) => state.offer.offers[offerState.offer.id],
  (offer: App.Offer): { duration: number, value: number, packageId: string }[] => {
    if (!offer?.packages) {
      return []
    }

    return offer.packages.map((option) => {
      if (option.isBaseRate) {
        return { duration: option.duration, value: option.value, packageId: option.id }
      }
    }).filter(excludeNullOrUndefined)
  },
)

export const getCheapestSelectedPackageOptionWithDefault = createSelector(
  getCheapestSelectedPackageOption,
  (state: App.State, offerState: OfferPageState) => getCheapestAvailablePackageOption(offerState, state),
  (cheapestSelectedPackage, cheapestAvailablePackage) => cheapestSelectedPackage ?? cheapestAvailablePackage,
)

export const getPackagesForFilters = createSelector(
  (state: App.State, offerState: OfferPageState) => getBasePackageViews(offerState, state),
  (_state: App.State, offerState: OfferPageState) => offerState.room,
  (_state: App.State, offerState: OfferPageState) => offerState.duration,
  (packageViews, room, duration) => {
    return packageViews.filter(view => {
      let filter = true
      if (duration) {
        filter = filter && view.package.duration === duration
      }

      if (room) {
        filter = filter && view.package.roomType?.id === room.id
      }

      return filter
    })
  },
)

export interface CalendarPickerMonthView {
  key: string;
  month: string;
  year: string;
  yearNum: number;
  monthIndex: number;
  hotelTotal: number;
  packagedTotal: number;
  hotelAvailable: boolean;
  packagedAvailable: boolean;
  limitedAvailability: boolean;
  loading?: boolean;
  flightCacheActive: boolean;
  hasPromo: boolean;
  hasTactical: boolean;
}

export const getFlightDealIdsByYearMonth = createSelector(
  (state: App.State, offerState: OfferPageState) => getRelevantMonths(state, offerState),
  (relevantMonths) => {
    const dealIds = relevantMonths.reduce((resultMap, monthView) => {
      const filteredDays = monthView.days.filter(day => !!day.dealId)
      filteredDays.forEach((day) => {
        if (day.dealId) {
          if (!(day.year in resultMap)) {
            resultMap[day.year] = { }
          }
          resultMap[day.year][day.month] = day.dealId
        }
        return resultMap
      })
      return resultMap
    }, {} as Record<string, Record<string, string>>)
    return {
      dealIds,
    }
  },
)

export const getCalendarMonths = createSelector(
  (state: App.State, offerState: OfferPageState) => offerState.package,
  (state: App.State, offerState: OfferPageState) => offerState.room,
  (state: App.State, offerState: OfferPageState) => offerState.offer?.bundledWithFlightsOnly,
  (state: App.State, offerState: OfferPageState) => getRelevantMonths(state, offerState),
  (state: App.State, offerState: OfferPageState) => isFetchingCalendars(state, offerState),
  (state: App.State, offerState: OfferPageState) => shouldEnableCalendarRecommendations(state, offerState.offer),
  (pkg, room, bundledWithFlights, relevantMonths, fetching, enableCalendarRecommendations): { months: Array<CalendarPickerMonthView>, availableCount: number } => {
    if (fetching) {
      // setup placeholder months when we're fetching
      const months = fillArray(18).map<CalendarPickerMonthView>((monthIncrement) => {
        const thisMonth = moment().add(monthIncrement, 'months')
        const year = thisMonth.year().toString()
        const month = thisMonth.format('MMMM')
        return {
          key: `${month}-${year}`,
          year,
          yearNum: thisMonth.year(),
          monthIndex: thisMonth.month(),
          month,
          loading: true,
          packagedTotal: 0,
          hotelTotal: 0,
          limitedAvailability: false,
          hotelAvailable: true,
          packagedAvailable: true,
          flightCacheActive: true,
          hasPromo: false,
          hasTactical: false,
        }
      })
      return {
        months,
        availableCount: months.length,
      }
    } else {
      const monthsByYearAndMonth = groupBy(relevantMonths, month => `${month.month}-${month.year}`)
      const views = Array.from(monthsByYearAndMonth.values()).map((allMonthViews): CalendarPickerMonthView => {
        let months: Array<App.CalendarMonth> = []
        if (pkg) {
          months = allMonthViews.filter(m => m.cheapestDay?.uniqueKey === pkg.uniqueKey)
        } else if (room) {
          months = allMonthViews.filter(m => m.cheapestDay?.roomId === room.id)
        } else {
          months = allMonthViews
        }

        const hotelDays = months.map(month => month.cheapestDay).filter(Boolean)
        const flightDays = months.map(month => month.cheapestDayWithFlights).filter(Boolean)
        let hotelTotal = hotelDays.length ? Math.min(...hotelDays.map(day => day.hotelTotal)) : 0
        let packagedTotal = flightDays.length ? Math.min(...flightDays.map(day => day.packagedTotal)) : 0
        if (bundledWithFlights && !months[0]?.flightCacheActive) {
          // bundling with flights means we have to have a flight for it to have a price
          // equal package and hotel total means no flight prices!
          packagedTotal = packagedTotal !== hotelTotal ? packagedTotal : 0
          hotelTotal = 0
        }
        const daysByDay = groupBy(months.flatMap(m => m.days), d => d.day)
        const daysAvailable = countWhere(daysByDay.values(), days => days.some(d => d.hotelAvailable))

        const hasDealId = months[0]?.days.some(day => !!day.dealId)

        return {
          key: allMonthViews[0].days[0].monthKey,
          hotelAvailable: hotelDays.some(d => d.hotelAvailable),
          packagedAvailable: flightDays.some(d => d.flightsAvailable),
          hotelTotal,
          packagedTotal,
          year: allMonthViews[0].year,
          month: allMonthViews[0].month,
          yearNum: allMonthViews[0].yearNum,
          monthIndex: allMonthViews[0].monthIndex,
          // considered limited availability if only 20% of the days of the month are available
          limitedAvailability: daysAvailable > 0 && daysAvailable <= (allMonthViews[0].days.length * 0.2),
          flightCacheActive: months[0]?.flightCacheActive,
          hasPromo: hasDealId,
          hasTactical: months.some(m => m.days.some(d => d.hasTactical && d.hotelAvailable)),
        }
      })

      const availableCalendarMonths = trimArray(views, view => !view.hotelAvailable)
      return {
        months: enableCalendarRecommendations ? views : availableCalendarMonths,
        availableCount: availableCalendarMonths.length,
      }
    }
  },
)

export const getBundleCalendarMonths = createSelector(
  (state: OfferPageState) => state,
  (state: OfferPageState, appState: App.State) => appState,
  (state, appState): { months: CalendarPickerMonthView[], availableCount: number } => {
    const allCalendarMonths = coverByOffersStates<{ months: Array<CalendarPickerMonthView>, availableCount: number }>(getCalendarMonths, appState, state).map(x => x.months)

    const bundleOfferCalendarMonths = allCalendarMonths.reduce((resultMonths, months, index) => {
      if (index === 0) {
        return arrayToObject(months, month => month.key)
      } else {
        const lastOfferMonths = Object.values(resultMonths)
        const nextOfferMonths = arrayToObject(months, month => month.key)
        for (const lastMonth of lastOfferMonths) {
          const month = nextOfferMonths[lastMonth.key]
          if (month) {
            resultMonths[month.key] = {
              ...resultMonths[month.key],
              hotelAvailable: resultMonths[month.key].hotelAvailable && month.hotelAvailable,
              packagedAvailable: resultMonths[month.key].packagedAvailable && month.packagedAvailable,
              hotelTotal: resultMonths[month.key].hotelTotal + month.hotelTotal,
              packagedTotal: resultMonths[month.key].packagedTotal + month.packagedTotal,
              limitedAvailability: resultMonths[month.key].limitedAvailability && month.limitedAvailability,
              flightCacheActive: resultMonths[month.key].flightCacheActive && month.flightCacheActive,
            }
          } else {
            resultMonths[lastMonth.key] = {
              ...resultMonths[lastMonth.key],
              hotelAvailable: false,
              packagedAvailable: false,
            }
          }
        }
      }
      return resultMonths
    }, {} as { [key: string]: CalendarPickerMonthView })

    return {
      months: Object.values(bundleOfferCalendarMonths),
      availableCount: trimArray(Object.values(bundleOfferCalendarMonths), month => !month.hotelAvailable).length,
    }
  },
)

export const getDaysOtherAvailability = createSelector(
  (state: App.State, offerState: OfferPageState) => offerState.offer,
  (state: App.State, offerState: OfferPageState) => getRelevantDays(state, offerState),
  (offer, relevantDays) => {
    // now find if there are hotels availble on this day
    return relevantDays.reduce((currentObject, day) => {
      if (currentObject[day.checkIn]) {
        return currentObject
      }

      currentObject[day.checkIn] = offer.bundledWithFlightsOnly ? (day.flightsAvailable && day.hotelAvailable) : day.hotelAvailable

      return currentObject
    }, {})
  },
)

export const getMonthsOtherAvailability = createSelector(
  (state: App.State, offerState: OfferPageState) => offerState.offer,
  (state: App.State, offerState: OfferPageState) => getRelevantDays(state, offerState),
  (offer, relevantDays) => {
    // group up the days so we can get the cheapest one
    // now find if there are hotels availble on this day

    return relevantDays.reduce((currentObject, day) => {
      if (currentObject[day.monthKey]) {
        return currentObject
      }

      currentObject[day.monthKey] = offer.bundledWithFlightsOnly ? (day.flightsAvailable && day.hotelAvailable) : day.hotelAvailable

      return currentObject
    }, {})
  },
)

export const getOffersAvailableRates = createSelector(
  (state: App.State) => state.offer.offerAvailableRates,
  (state: App.State, offerState: OfferPageState) => offerState.bundledOffers,
  (state: App.State, offerState: OfferPageState) => offerState.rooms,
  (offerAvailableRates, bundledOffers, rooms) => {
    if (offerAvailableRates && bundledOffers) {
      return Object.values(bundledOffers).map((o) => {
        const availableRatesKey = o.checkInDate && rooms ? buildParamsKey(o.checkInDate.format(ISO_DATE_FORMAT), o.checkInDate.clone().add(o.duration, 'days').format(ISO_DATE_FORMAT), rooms) : undefined
        return availableRatesKey && offerAvailableRates?.[o.offer.id]?.[availableRatesKey]
      })
    }

    return null
  },
)

export const areCalendarsLoading = createSelector(
  (state: App.State) => state.calendar.calendarsLoading,
  (state: App.State, offerState: OfferPageState) => offerState.offer,
  getOffersAvailableRates,
  (state: App.State, offerState: OfferPageState) => getBundledPackages(offerState),
  (calendars, offer, offersAvailableRates, bundlePackages) => {
    if (bundlePackages) {
      const packagesData = Object.values(bundlePackages).flat()
      const allAllowPackages = packagesData.map(pkg => pkg.uniqueKey)

      return allAllowPackages.some((uniqueKey) => calendars[uniqueKey]) || offersAvailableRates.some((o) => o?.fetching)
    }

    const packages = (offer.packages.length === 0 && offer?.lowestPricePackage) ? [offer.lowestPricePackage] : offer.packages
    return !!(calendars[offer.id] || packages.some(pkg => calendars[pkg.uniqueKey]))
  },
)

/**
 * @deprecated
 * This is only used with the old checkout
 *
 * @remarks
 * Replaced by getInitialLeOfferPageState
 */
export const getLEOfferPageDefaults = createSelector(
  (state: App.State) => state.cart.accommodation.offer,
  (state: App.State) => state.cart.accommodation.package,
  (state: App.State) => state.cart.accommodation.occupancies,
  (state: App.State) => state.cart.accommodation.startDate,
  (state: App.State) => state.cart.flight.flightOrigin,
  (state: App.State) => state.cart.accommodation.reservationType,
  (state: App.State) => state.geo.originAirports,
  (state: App.State) => state.router.location.search,
  (state: App.State) => getDefaultAirport(state),
  (state: App.State, currentOffer: App.Offer) => currentOffer,
  (
    offerFromCart,
    pkgFromCart,
    roomsFromCart,
    startDateFromCart,
    flightOriginCode,
    reservationTypeFromCart,
    airports,
    encodedSearchParams,
    defaultAirport,
    currentOffer: App.Offer,
  ): Partial<OfferPageState> => {
    const searchParams = decodeSearchParams(encodedSearchParams, getOfferAndFlightsMaxChildAge(currentOffer), currentOffer.property.maxInfantAge)

    const hasDateParams = searchParams.dates.checkIn && searchParams.dates.checkOut
    const hasThisOfferInCart = currentOffer.id === offerFromCart?.id &&
      reservationTypeFromCart === RESERVATION_TYPE_INSTANT_BOOKING

    const defaults: Partial<OfferPageState> = {
      airport: defaultAirport,
      multipleRoomPackages: [],
    }

    if (hasThisOfferInCart) {
      defaults.airport = airports.find(airport => airport.code === flightOriginCode) ?? defaultAirport
    }

    defaults.duration = currentOffer?.lowestPricePackage?.duration
    if (hasDateParams) {
      defaults.checkInDate = moment(searchParams.dates.checkIn)
      const checkOut = moment(searchParams.dates.checkOut)
      defaults.duration = Math.round(moment.duration(checkOut.diff(defaults.checkInDate)).asDays())
    } else if (hasThisOfferInCart && startDateFromCart) {
      defaults.checkInDate = moment(startDateFromCart).isAfter(Date.now()) ? moment(startDateFromCart) : undefined
      defaults.duration = pkgFromCart?.duration
    }

    if (searchParams.rooms.length > 0) {
      defaults.rooms = searchParams.rooms.map(generateRoomOccupants)
    } else if (hasThisOfferInCart) {
      defaults.rooms = roomsFromCart.map(generateRoomOccupants)
    }

    if (hasThisOfferInCart && pkgFromCart) {
      defaults.room = currentOffer.packages.find(p => p.uniqueKey === pkgFromCart.uniqueKey)?.roomType
    }

    return defaults
  },
)

export const getFromDurationLabel = createSelector(
  (state: OfferPageState) => state.offer,
  getAvailablePackageOptions,
  getCheapestAvailablePackageOption,
  (state: OfferPageState, appState: App.State) => getCheapestSelectedPackageOption(appState, state),
  (state: OfferPageState, appState: App.State) => getMarketedLOS(state, appState),
  (state: OfferPageState) => state.duration,
  (offer, availableOptions, cheapestAvailableOption, cheapestSelectedOption, currentRegionMarketedLos, duration): string => {
    // coming from search page or has selectdd duration in offer page
    if (duration) {
      return `${pluralizeToString('night', duration)} from`
    }
    if (currentRegionMarketedLos) {
      if (currentRegionMarketedLos.package.duration === 1) {
        // Show no duration label when starts with a 1 night, only the from
        return 'from'
      }
      return `${offer?.durationLabel} from`
    }

    if (cheapestSelectedOption) {
      return `${pluralizeToString('night', cheapestSelectedOption.package.duration)} from`
    } else if (offer?.lowestPricePackage?.duration === cheapestAvailableOption.package.duration) {
      if (offer.lowestPricePackage.duration === 1) {
        // Show no duration label when starts with a 1 night, only the from
        return 'from'
      }
      return `${offer.durationLabel} from`
    } else {
      const durations = new Set(availableOptions.map(view => view.package.duration))
      if (durations.has(1)) {
        // Show no duration label when starts with a 1 night, only the from
        return 'from'
      }
      return `${getDurationLabel(Array.from(durations))} from`
    }
  },
)

export const getPropertyIdIfBestOfferRedirectRequired = createSelector(
  (state: App.State, offerId: string) => state.offer.offers[offerId],
  (offer) => {
    if (
      // config.ENABLE_BEST_OFFER_REDIRECTION &&
      offer?.property
    ) {
      return offer.property.id
    }
  },
)

export const getPropertyAlternateOfferIds = createSelector(
  (state: App.State, offerId: string) => (state.offer.offers[offerId] || state.offer.offerSummaries[offerId])?.property?.id,
  (state: App.State) => state.offer.alternativeOffersForProperty,
  (propertyId, alternativeOffersForProperty) => {
    if (propertyId) {
      return alternativeOffersForProperty[propertyId]
    }
  },
)

export const getPropertyBestAlternateOfferIds = createSelector(
  (state: App.State, offerId: string) => getPropertyIdIfBestOfferRedirectRequired(state, offerId),
  (state: App.State) => state.offer.alternativeOffersForProperty,
  (propertyId, alternativeOffersForProperty) => {
    if (propertyId) {
      return alternativeOffersForProperty[propertyId]
    }
  },
)

export const getPropertyAlternateOffer = createSelector(
  (state: App.State, offerId: string) => getPropertyAlternateOfferIds(state, offerId),
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.bedbankOffers,
  (alternatePropertyOffers, offers, bedbankOffers) => {
    if (alternatePropertyOffers) {
      if (alternatePropertyOffers.le.length > 0) {
        return offers[alternatePropertyOffers.le[0]]
      } else if (alternatePropertyOffers.bedbank.length > 0) {
        return bedbankOffers[alternatePropertyOffers.bedbank[0]]
      }
    }
  },
)

export const getPropertyBestAlternateOffer = createSelector(
  (state: App.State, offerId: string) => getPropertyBestAlternateOfferIds(state, offerId),
  (state: App.State) => state.offer.offers,
  (state: App.State, offerId: string) => offerId,
  (alternatePropertyOffers, offers, offerId) => {
    if (alternatePropertyOffers?.le && alternatePropertyOffers.le.length > 0 && offers[offerId].type !== 'hotel') {
      const alt = alternatePropertyOffers.le.find((offer:string) => offer !== offerId)
      if (alt && offers[offerId].type !== offers[alt]?.type) {
        return offers[alt]
      }
    }
  },
)

export const getPropertyBedbankOffer = createSelector(
  (state: App.State, offerId: string) => getPropertyAlternateOfferIds(state, offerId),
  (state: App.State) => state.offer.bedbankOffers,
  (alternatePropertyOffers, bedbankOffers) => {
    if (alternatePropertyOffers?.bedbank.length > 0) {
      return bedbankOffers[alternatePropertyOffers.bedbank[0]]
    }
  },
)

export const getPropertyAlternateURL = createSelector(
  (state: App.State, offerId: string) => getPropertyAlternateOffer(state, offerId),
  (state: App.State) => state.geo.currentRegionCode,
  (offer, regionCode) => {
    if (offer) {
      if (offer.type === 'bedbank_hotel') {
        return `/${regionCode.toLowerCase()}/partner/${offer.slug}/${offer.id}`
      } else {
        return `/${regionCode.toLowerCase()}/offer/${offer.slug}/${offer.id}`
      }
    }
  },
)

export const getPropertyBestAlternateURL = createSelector(
  (state: App.State, offerId: string) => getPropertyBestAlternateOffer(state, offerId),
  (state: App.State) => state.geo.currentRegionCode,
  (offer, regionCode) => {
    if (offer) {
      return `/${regionCode.toLowerCase()}/offer/${offer.slug}/${offer.id}`
    }
  },
)

export const isAvailableForPurchase = createSelector(
  (state: OfferPageState) => state.offer,
  getPackageViews,
  (offer, views) => {
    return scheduleIsCurrent(offer.onlinePurchaseSchedule) && views.some(p => p.hotelAvailable)
  },
)

/** @description Room rates have a variety of inclusions (base & bonus) from different sources attached to them */
/* this object structure maps room rate ids to a collection of iso dates that happen to be in tactical schedule */
/* range for an inclusion or rate plan they're structured this way instead of an array for faster lookup */
export const getTacticalScheduleMap = createSelector(
  (state: OfferPageState, appState: App.State) => getRelevantPackages(state, appState),
  (packageViews) => {
    const isCalendarDayValidTactical = roomRateTacticalScheduleMemoiser(packageViews.map(p => p.package))
    return isCalendarDayValidTactical
  },
)

export const isBNBLAvailable = createSelector(
  (state: OfferPageState) => state.offer,
  (offer) => offer.hasBuyNowBookLaterPackages &&
    offer.hasBuyNowBookLater &&
    !!offer.travelToDate &&
    config.accommodation.hasBnbl,
)

export const isFetchingCalendars = createSelector(
  (appState: App.State) => appState.calendar.calendarsByPackageKey,
  (appState: App.State, pageState: OfferPageState) => getRelevantPackages(pageState, appState),
  (calendars, packages) => {
    return packages.some(pkg => !calendars[pkg.package.uniqueKey])
  },
)

const getInitialLEHotelStateFromCheckout = createSelector(
  (state: App.State) => getLeHotelItems(state),
  (state: App.State) => getVillaItems(state),
  getFlightItems,
  (_state: App.State, offer: App.Offer) => offer,
  (state: App.State, offer: App.Offer) => multiRoomEnabled(state, offer),
  (state: App.State) => state.checkout.cart.roomsToBeConnected,
  (accommodationItems, villaItems, flightItems, offer, isMultiRoomEnabled, connectedMultiRooms): Partial<OfferPageState> => {
    const items = [...accommodationItems, ...villaItems].filter(item => item.offerId === offer.id)
    if (items.length === 0) { return null }

    // Assumption: Only one unique accommodation offer
    const referenceItem = items[0]
    const roomType = offer.packages.find(p => p.id === referenceItem.packageId)?.roomType

    const rooms = items.map(item => {
      if (item.reservationType !== RESERVATION_TYPE_INSTANT_BOOKING) { return null }
      return generateRoomOccupants(item.occupancy)
    }).filter(excludeNullOrUndefined)

    const multipleRoomPackages = (isMultiRoomEnabled ? items : []).map(item => {
      if (
        item.reservationType !== RESERVATION_TYPE_INSTANT_BOOKING ||
        !('roomId' in item.occupancy)) { return null }

      const pkg = offer.packages.find(p => p.id === item.packageId)
      if (!pkg) { return null }

      const bundledFlightItem = flightItems.find(item => item.bundledItemIds?.includes(item.itemId))
      return {
        roomId: item.occupancy.roomId,
        roomTypeId: roomType.id,
        isFlightsSelected: !!bundledFlightItem,
        packageUniqueKey: pkg.uniqueKey,
      }
    }).filter(excludeNullOrUndefined)

    const result = {
      ...('checkIn' in referenceItem && { checkInDate: moment(referenceItem.checkIn) }),
      ...('checkOut' in referenceItem && { checkOutDate: moment(referenceItem.checkOut) }),
      duration: items[0].duration,
      ...(rooms.length > 0 && { rooms }),
      ...(roomType && { room: roomType }),
      multipleRoomPackages,
      ...(isMultiRoomEnabled && { isMultiRoomConnected: (connectedMultiRooms?.length ?? 0) > 0 }),
    }

    return result
  },
)

const getInitialLEHotelState = createSelector(
  getInitialLEHotelStateFromCheckout,
  (_state: App.State, offer: App.Offer) => offer,
  (accommodationState, offer): Partial<OfferPageState> => {
    return {
      multipleRoomPackages: [],
      duration: offer.lowestPricePackage?.duration,
      ...accommodationState,
    }
  },
)

const getInitialBundleAndSaveState = createSelector(
  (_state: App.State, offer: App.Offer) => offer,
  (offer): Partial<OfferPageState> => {
    const bundledOffers = Object.values(offer.bundledOffers)
    const lowestPriceBundlePackage = offer.lowestPriceBundlePackage

    const allOffersIds = Object.keys(offer.bundledOffers)

    const currentOfferId = allOffersIds[0]

    return {
      multipleRoomPackages: [],
      paginator: {
        currentOfferId,
        nextOfferId: allOffersIds[allOffersIds.indexOf(currentOfferId) + 1],
        backOfferId: allOffersIds[allOffersIds.indexOf(currentOfferId) - 1],
      },
      duration: lowestPriceBundlePackage.duration,
      filteredBundlePackageId: undefined,
      checkInDate: undefined,
      checkOutDate: undefined,
      bundleOffer: isBundleOffer(offer) ? offer : undefined,
      offer,
      bundledOffers: bundledOffers.reduce<{ [offerId: string]: BundledOfferState }>((acc, item) => {
        const packages = offer.packages.filter((p) => p.offerId === item.id)
        const pkg = lowestPriceBundlePackage.packages.find((x) => x.offerId === item.id)

        const attachedOffer = {
          ...offer,
          ...item,
          bundlePackages: undefined,
          bundledOffers: undefined,
          packages: packages.length ? packages : [pkg],
        }

        const lowestPricePackage = getLowestPricePackage(attachedOffer, attachedOffer.packages)

        acc[item.id] = {
          duration: pkg.duration,
          offer: {
            ...attachedOffer,
            lowestPricePackage,
          },
        }
        return acc
      }, {}),
      bundleSwapModalTicker: 0,
    }
  },
)

const getInitialBedbankState = createSelector(
  (state: App.State) => getBedbankItems(state),
  (items): Partial<OfferPageState> => {
    if (items.length === 0) {
      return {
        // Certain parts of the bedbank offer page assume `rooms` is always defined,
        // even though it's typed as optional. So the initial state needs to include it.
        rooms: [generateRoomOccupants()],
      }
    }
    const referenceItem = items[0]
    const rooms = items.map(item => generateRoomOccupants(item.occupancy))

    return {
      ...('checkIn' in referenceItem && { checkInDate: moment(referenceItem.checkIn) }),
      ...('checkOut' in referenceItem && { checkOutDate: moment(referenceItem.checkOut) }),
      rooms,
    }
  },
)

const getInitialFlightState = createSelector(
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.geo.originAirports,
  (state: App.State) => getDefaultAirport(state),
  (state: App.State) => state.flights.journeys,
  (items, airportList, defaultAirport, journeys): Partial<OfferPageState> => {
    // Assumption: Only one unique flight
    if (items.length === 0) { return { airport: defaultAirport } }
    const referenceItem = items[0]
    const departureJourney = journeys.find(journey => journey.id === referenceItem.searchId)
    if (!departureJourney) return { airport: defaultAirport }

    const departureAirport = departureJourney.departing.flights[0].departingAirport
    return {
      airport: airportList.find(airport => airport.code === departureAirport) ?? defaultAirport,
    }
  },
)

const getInitialSearchState = createSelector(
  (state: App.State) => state.router.location.search,
  (_state: App.State, offer: App.Offer | App.BedbankOffer) => offer,
  (rawSearchParams, offer) => {
    const args = isLEHotel(offer) || isBundleOffer(offer) ?
        [getOfferAndFlightsMaxChildAge(offer as App.Offer), (offer as App.Offer).property.maxInfantAge] :
        [getBedbankAndFlightsChildMaxAge(offer as App.BedbankOffer)]

    const searchParams = decodeSearchParams(
      rawSearchParams,
      ...args,
    )

    const result: Partial<OfferPageState> = {
      ...(searchParams.rooms.length > 0 && { rooms: searchParams.rooms.map(generateRoomOccupants) }),
    }

    if (searchParams.dates.checkIn) {
      result.checkInDate = moment(searchParams.dates.checkIn)

      if (searchParams.dates.checkOut) {
        result.checkOutDate = moment(searchParams.dates.checkOut)
        result.duration = Math.round(moment.duration(result.checkOutDate.diff(result.checkInDate)).asDays())
      }
    }

    if (searchParams.bundledOfferId && searchParams.dates.checkIn && searchParams.dates.checkOut) {
      result.bundledOffers = {
        [searchParams.bundledOfferId]: {
          checkInDate: result.checkInDate,
          checkOutDate: result.checkOutDate,
          duration: result.duration,
        },
      }
    }

    return result
  },
)

export const getInitialBundleAndSaveOfferPageState = createSelector(
  (state: App.State, offer: App.Offer) => getInitialBundleAndSaveState(state, offer),
  (state: App.State, offer: App.Offer) => getInitialSearchState(state, offer),
  (accommodationState, searchState): Partial<OfferPageState> => {
    if (searchState.bundledOffers) {
      const bundledOfferId = Object.keys(searchState.bundledOffers)[0]
      let bundledOffers = accommodationState.bundledOffers ?? {}

      if (bundledOffers[bundledOfferId]) {
        const searchBundledOffer = searchState.bundledOffers[bundledOfferId]
        const packages = accommodationState.bundleOffer?.packages?.filter((pkg) => !searchState.rooms?.length || searchState.rooms?.every(room => isReservationAvailableForRoom(room, pkg.roomRate.capacities, accommodationState.bundleOffer?.bundledOffers[pkg.offerId])))
        const allPackagesBySearchOfferId = packages?.filter(x => x.offerId === bundledOfferId && x.duration === searchBundledOffer.duration) ?? []
        const allOtherPackages = packages?.filter(x => x.offerId !== bundledOfferId) ?? []

        const bundledData = getBundlePackagesWithPrice({
          ...accommodationState.bundleOffer,
          packages: [...allPackagesBySearchOfferId, ...allOtherPackages],
        })

        if (bundledData?.length) {
          const minBundledData = min(bundledData, pkg => pkg.price || Number.MAX_SAFE_INTEGER)
          bundledOffers = Object.keys(bundledOffers).reduce((acc, bundledOfferId) => {
            if (!acc[bundledOfferId]) {
              const availablePkg = minBundledData?.packages?.find(x => x.offerId === bundledOfferId)

              if (availablePkg) {
                acc[bundledOfferId] = {
                  ...bundledOffers[bundledOfferId],
                  duration: availablePkg.duration,
                }
              } else {
                acc[bundledOfferId] = bundledOffers[bundledOfferId]
              }
            }
            return acc
          }, {
            [bundledOfferId]: {
              ...bundledOffers[bundledOfferId],
              ...searchBundledOffer,
            },
          })
        }
      }

      return {
        ...accommodationState,
        rooms: searchState.rooms?.length ? [searchState.rooms[0]] : undefined,
        duration: sum(Object.values(bundledOffers), d => d.duration),
        bundledOffers: {
          ...bundledOffers,
        },
      }
    }

    return {
      ...accommodationState,
      rooms: searchState.rooms?.length ? [searchState.rooms[0]] : undefined,
    }
  },
)

export const getInitialLeOfferPageState = createSelector(
  (state: App.State, offer: App.Offer) => getInitialLEHotelState(state, offer),
  (state: App.State, offer: App.Offer) => getInitialSearchState(state, offer),
  (state: App.State) => getInitialFlightState(state),
  (accommodationState, searchState, flightState): Partial<OfferPageState> => {
    // The order of the spread elements matter!
    return {
      ...accommodationState,
      ...searchState,
      ...flightState,
    }
  },
)

export const getInitialBedbankOfferPageState = createSelector(
  (state: App.State, _offer: App.BedbankOffer) => getInitialBedbankState(state),
  (state: App.State, offer: App.BedbankOffer) => getInitialSearchState(state, offer),
  (_state: App.State, offer: App.BedbankOffer) => getPromotionMinLos({ bedbankOffer: offer }),
  (state: App.State) => getInitialFlightState(state),
  (bedbankState, searchState, promotionMinLos, flightState) => {
    // The order of the spread elements matter! It certainly does!
    return {
      ...bedbankState,
      ...searchState,
      ...flightState,
      duration: searchState.duration ? searchState.duration : promotionMinLos,
    }
  },
)

/**
 * Whether or not there's enough information for an offer page to be able to continue to checkout
 */
export const canBookNow = createSelector(
  (state: OfferPageState, appState: App.State) => getSelectedOfferPageDay(appState, state),
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.selectedPackageView,
  (state: OfferPageState) => state.offer,
  (selectedDay, rooms = [], selectedPackageView, offer) => {
    if (isVillaOffer(offer)) {
      // villas don't have multiple packages, so dont' require a 'selected package view' - they are always selected
      return !!selectedDay && rooms.length > 0 && !isRoomsInvalid(rooms) && selectedDay.hotelAvailable
    }
    return selectedPackageView && !offer?.property?.useDynamicCancellationPolicies
  },
)
